import React from "react";
import { buildIntroOuterVariants } from "./trigIntroOuterVariants";
import { BlockMath, InlineMath } from "react-katex";
import PiInput, {
  formatAffineLatexPreview,
  formatPiValueLatex,
  parsePiLikeInput,
} from "../../../components/inputs/PiInput";
import SelectInput from "../../../components/inputs/SelectInput";

export const type = "TrigEquationIntroTask";

function gcdPair(a, b) { let x = Math.abs(a); let y = Math.abs(b); while (y) [x, y] = [y, x % y]; return x || 1; }
function pair(n, d = 1) { const sign = d < 0 ? -1 : 1; const divisor = gcdPair(n, d); return [(n * sign) / divisor, Math.abs(d) / divisor]; }
function subtractPair([an, ad], [bn, bd]) { return pair(an * bd - bn * ad, ad * bd); }
function dividePair([an, ad], [bn, bd]) { return pair(an * bd, ad * bn); }

function piPairLatex([n, d]) {
  if (n === 0) return "0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (d === 1) return abs === 1 ? `${sign}\\pi` : `${sign}${abs}\\pi`;
  return `${sign}\\dfrac{${abs === 1 ? "\\pi" : `${abs}\\pi`}}{${d}}`;
}

function periodTermLatex(period) {
  const latex = piPairLatex(period);
  return latex === "\\pi" ? "k\\pi" : `k\\cdot${latex}`;
}

function familyPairLatex([anchor, period], variable = "x") {
  if (anchor[0] === 0) return `${variable}=${periodTermLatex(period)}`;
  return `${variable}=${piPairLatex(anchor)}+${periodTermLatex(period)}`;
}

const functionLatex = (fn) => fn === "tan" ? "\\operatorname{tg}" : fn === "cot" ? "\\operatorname{ctg}" : `\\${fn}`;
const directVariant = (fn, target, targetLatex, families) => ({
  equationLatex: `${functionLatex(fn)} x=${targetLatex}`,
  fn,
  target,
  targetLatex,
  families,
});

const DIRECT_POSITIVE = [
  directVariant("sin", 0, "0", [[[0, 1], [1, 1]]]),
  directVariant("sin", 0.5, "\\dfrac12", [[[1, 6], [2, 1]], [[5, 6], [2, 1]]]),
  directVariant("sin", Math.sqrt(2) / 2, "\\dfrac{\\sqrt2}{2}", [[[1, 4], [2, 1]], [[3, 4], [2, 1]]]),
  directVariant("sin", Math.sqrt(3) / 2, "\\dfrac{\\sqrt3}{2}", [[[1, 3], [2, 1]], [[2, 3], [2, 1]]]),
  directVariant("sin", 1, "1", [[[1, 2], [2, 1]]]),
  directVariant("cos", 0, "0", [[[1, 2], [1, 1]]]),
  directVariant("cos", 0.5, "\\dfrac12", [[[-1, 3], [2, 1]], [[1, 3], [2, 1]]]),
  directVariant("cos", Math.sqrt(2) / 2, "\\dfrac{\\sqrt2}{2}", [[[-1, 4], [2, 1]], [[1, 4], [2, 1]]]),
  directVariant("cos", Math.sqrt(3) / 2, "\\dfrac{\\sqrt3}{2}", [[[-1, 6], [2, 1]], [[1, 6], [2, 1]]]),
  directVariant("cos", 1, "1", [[[0, 1], [2, 1]]]),
  directVariant("tan", 0, "0", [[[0, 1], [1, 1]]]),
  directVariant("tan", Math.sqrt(3) / 3, "\\dfrac{\\sqrt3}{3}", [[[1, 6], [1, 1]]]),
  directVariant("tan", 1, "1", [[[1, 4], [1, 1]]]),
  directVariant("tan", Math.sqrt(3), "\\sqrt3", [[[1, 3], [1, 1]]]),
  directVariant("cot", 0, "0", [[[1, 2], [1, 1]]]),
  directVariant("cot", Math.sqrt(3) / 3, "\\dfrac{\\sqrt3}{3}", [[[1, 3], [1, 1]]]),
  directVariant("cot", 1, "1", [[[1, 4], [1, 1]]]),
  directVariant("cot", Math.sqrt(3), "\\sqrt3", [[[1, 6], [1, 1]]]),
];

const DIRECT_NEGATIVE = [
  directVariant("sin", -0.5, "-\\dfrac12", [[[-1, 6], [2, 1]], [[-5, 6], [2, 1]]]),
  directVariant("sin", -Math.sqrt(2) / 2, "-\\dfrac{\\sqrt2}{2}", [[[-1, 4], [2, 1]], [[-3, 4], [2, 1]]]),
  directVariant("sin", -Math.sqrt(3) / 2, "-\\dfrac{\\sqrt3}{2}", [[[-1, 3], [2, 1]], [[-2, 3], [2, 1]]]),
  directVariant("sin", -1, "-1", [[[-1, 2], [2, 1]]]),
  directVariant("cos", -0.5, "-\\dfrac12", [[[-2, 3], [2, 1]], [[2, 3], [2, 1]]]),
  directVariant("cos", -Math.sqrt(2) / 2, "-\\dfrac{\\sqrt2}{2}", [[[-3, 4], [2, 1]], [[3, 4], [2, 1]]]),
  directVariant("cos", -Math.sqrt(3) / 2, "-\\dfrac{\\sqrt3}{2}", [[[-5, 6], [2, 1]], [[5, 6], [2, 1]]]),
  directVariant("cos", -1, "-1", [[[1, 1], [2, 1]]]),
  directVariant("tan", -Math.sqrt(3) / 3, "-\\dfrac{\\sqrt3}{3}", [[[-1, 6], [1, 1]]]),
  directVariant("tan", -1, "-1", [[[-1, 4], [1, 1]]]),
  directVariant("tan", -Math.sqrt(3), "-\\sqrt3", [[[-1, 3], [1, 1]]]),
  directVariant("cot", -Math.sqrt(3) / 3, "-\\dfrac{\\sqrt3}{3}", [[[-1, 3], [1, 1]]]),
  directVariant("cot", -1, "-1", [[[-1, 4], [1, 1]]]),
  directVariant("cot", -Math.sqrt(3), "-\\sqrt3", [[[-1, 6], [1, 1]]]),
];

const AFFINE_ARGUMENTS = [
  { a: [2, 1], b: [-1, 6] }, { a: [2, 1], b: [1, 4] }, { a: [2, 1], b: [-1, 3] },
  { a: [3, 1], b: [1, 6] }, { a: [3, 1], b: [-1, 4] }, { a: [3, 1], b: [1, 3] },
  { a: [1, 2], b: [-1, 6] }, { a: [1, 2], b: [1, 4] }, { a: [1, 2], b: [-1, 3] },
  { a: [1, 3], b: [1, 6] }, { a: [1, 3], b: [-1, 4] }, { a: [1, 3], b: [1, 3] },
];

function coefficientXPairLatex([n, d]) {
  const sign = n < 0 ? "-" : "";
  const absolute = Math.abs(n);
  if (d === 1) return `${sign}${absolute === 1 ? "" : absolute}x`;
  return `${sign}\\dfrac{${absolute === 1 ? "x" : `${absolute}x`}}{${d}}`;
}

function affineArgumentLatex(a, b) {
  const xPart = coefficientXPairLatex(a);
  if (b[0] === 0) return xPart;
  const piPart = piPairLatex([Math.abs(b[0]), b[1]]);
  return `${xPart}${b[0] < 0 ? "-" : "+"}${piPart}`;
}

function argumentVariant(base, { a, b }) {
  const argumentLatex = affineArgumentLatex(a, b);
  const families = base.families.map(([anchor, period]) => {
    const step = dividePair(period, a);
    return [dividePair(subtractPair(anchor, b), a), [Math.abs(step[0]), step[1]]];
  });
  const baseEquations = base.families.map((family) => `${argumentLatex}=${familyPairLatex(family, "u").slice(2)}`).join("\\quad\\lor\\quad{}");
  const shiftedEquations = base.families.map(([anchor, period]) => {
    const shifted = subtractPair(anchor, b);
    const right = shifted[0] === 0 ? periodTermLatex(period) : `${piPairLatex(shifted)}+${periodTermLatex(period)}`;
    return `${coefficientXPairLatex(a)}=${right}`;
  }).join("\\quad\\lor\\quad{}");
  const finalEquations = families.map((family) => familyPairLatex(family)).join("\\quad\\lor\\quad{}");
  return {
    ...base,
    equationLatex: `${functionLatex(base.fn)}\\left(${argumentLatex}\\right)=${base.targetLatex}`,
    a: a[0] / a[1],
    b: b[0] / b[1],
    argumentLatex,
    baseEquation: `${functionLatex(base.fn)} u=${base.targetLatex}`,
    baseFamilies: base.families,
    families,
    afterGraphSteps: [baseEquations, shiftedEquations, finalEquations],
  };
}

const ARGUMENT = [...DIRECT_POSITIVE, ...DIRECT_NEGATIVE].flatMap((base) => AFFINE_ARGUMENTS.map((affine) => argumentVariant(base, affine)));

const OUTER = buildIntroOuterVariants([...DIRECT_POSITIVE, ...DIRECT_NEGATIVE], argumentVariant, functionLatex, pair);

export const introVariantCounts = {
  directPositive: DIRECT_POSITIVE.length, directNegative: DIRECT_NEGATIVE.length,
  argument: ARGUMENT.length, outer: OUTER.length,
};
export const introVariantPools = {
  directPositive: DIRECT_POSITIVE, directNegative: DIRECT_NEGATIVE, argument: ARGUMENT, outer: OUTER,
};

const INTERVALS = [
  [[-1, 1], [1, 1]],
  [[0, 1], [2, 1]],
  [[-1, 2], [3, 2]],
  [[-2, 1], [0, 1]],
];
const pick = (items) => items[Math.floor(Math.random() * items.length)];
const TRIG_FUNCTIONS = ["sin", "cos", "tan", "cot"];
const pickByFunction = (items) => {
  const fn = pick(TRIG_FUNCTIONS);
  return pick(items.filter((item) => item.fn === fn));
};

function gcd(a, b) { let x = Math.abs(a); let y = Math.abs(b); while (y) [x, y] = [y, x % y]; return x || 1; }
function rat(n, d = 1) { const sign = d < 0 ? -1 : 1; const divisor = gcd(n, d); return { n: (n * sign) / divisor, d: Math.abs(d) / divisor }; }
const toRat = ([n, d]) => rat(n, d);
const ratValue = ({ n, d }) => n / d;
const addRat = (left, right) => rat(left.n * right.d + right.n * left.d, left.d * right.d);
const multiplyRat = (value, integer) => rat(value.n * integer, value.d);
const ratKey = (value) => `${value.n}/${value.d}`;
const normalizeFamilies = (families) => families.map(([anchor, period]) => ({ anchor: toRat(anchor), period: toRat(period) }));

function solutionsInInterval(families, interval) {
  const [left, right] = interval;
  const found = new Map();
  families.forEach(({ anchor, period }) => {
    for (let k = -30; k <= 30; k += 1) {
      const value = addRat(anchor, multiplyRat(period, k));
      const numeric = ratValue(value);
      if (numeric >= ratValue(left) - 1e-9 && numeric <= ratValue(right) + 1e-9) found.set(ratKey(value), value);
    }
  });
  return [...found.values()].sort((x, y) => ratValue(x) - ratValue(y));
}

function makeTask(stage, config, intervalPairs = null) {
  const families = normalizeFamilies(config.families);
  const baseFamilies = normalizeFamilies(config.baseFamilies || config.families);
  const interval = intervalPairs ? intervalPairs.map(toRat) : null;
  return { type, data: { ...config, a: config.a ?? 1, b: config.b ?? 0, stage, families, baseFamilies, interval, expected: interval ? solutionsInInterval(families, interval) : null, maxPoints: interval ? 2 : 1 } };
}

export function generateTask({ stage = "directPositive", variantIndex } = {}) {
  if (stage === "directNegative") return makeTask(stage, pickByFunction(DIRECT_NEGATIVE));
  if (stage === "argument" || stage === "outer") {
    const pool = stage === "argument" ? ARGUMENT : OUTER;
    return makeTask(stage, Number.isInteger(variantIndex)
      ? pool[((variantIndex % pool.length) + pool.length) % pool.length] : pickByFunction(pool));
  }
  if (stage === "interval") {
    for (let attempt = 0; attempt < 40; attempt += 1) {
      const task = makeTask(stage, pick([...ARGUMENT, ...OUTER]), pick(INTERVALS));
      if (task.data.expected.length > 0 && task.data.expected.length <= 5) return task;
    }
    return makeTask(stage, ARGUMENT[0], INTERVALS[0]);
  }
  return makeTask("directPositive", pickByFunction(DIRECT_POSITIVE));
}

export function piFractionLatex({ n, d }) {
  if (n === 0) return "0";
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  if (d === 1) return abs === 1 ? `${sign}\\pi` : `${sign}${abs}\\pi`;
  return `${sign}\\dfrac{${abs === 1 ? "\\pi" : `${abs}\\pi`}}{${d}}`;
}

function familyLatex({ anchor, period }, variable = "x") {
  const periodLatex = piFractionLatex(period);
  if (anchor.n === 0) return `${variable}=k\\cdot${periodLatex}`;
  return `${variable}=${piFractionLatex(anchor)}+k\\cdot${periodLatex}`;
}

const familiesLatex = (families) => families.map((family) => familyLatex(family, "x")).join("\\qquad\\lor\\qquad{}");
const baseFamiliesLatex = (families) => families.map((family) => familyLatex(family, "u")).join("\\qquad\\lor\\qquad{}");
const solutionSetLatex = (expected) => `\\left\\{${expected.map(piFractionLatex).join(",\\;")}\\right\\}`;
const intervalLatex = ([left, right]) => `\\left[${piFractionLatex(left)},${piFractionLatex(right)}\\right]`;

function evaluate(fn, angle) {
  if (fn === "sin") return Math.sin(angle);
  if (fn === "cos") return Math.cos(angle);
  if (fn === "tan") return Math.tan(angle);
  return 1 / Math.tan(angle);
}

function exactValueLatex(value) {
  const values = [
    [0, "0"],
    [Math.sqrt(3), "\\sqrt3"],
    [-Math.sqrt(3), "-\\sqrt3"],
    [Math.sqrt(3) / 2, "\\dfrac{\\sqrt3}{2}"],
    [-Math.sqrt(3) / 2, "-\\dfrac{\\sqrt3}{2}"],
    [Math.sqrt(3) / 3, "\\dfrac{\\sqrt3}{3}"],
    [-Math.sqrt(3) / 3, "-\\dfrac{\\sqrt3}{3}"],
    [Math.sqrt(2) / 2, "\\dfrac{\\sqrt2}{2}"],
    [-Math.sqrt(2) / 2, "-\\dfrac{\\sqrt2}{2}"],
    [0.5, "\\dfrac12"],
    [-0.5, "-\\dfrac12"],
    [1, "1"],
    [-1, "-1"],
  ];
  return values.find(([candidate]) => Math.abs(candidate - value) < 1e-9)?.[1] || String(value);
}

function cotangentConversionLatex(target) {
  if (Math.abs(target) < 1e-9) return "\\operatorname{ctg}\\alpha=0\\quad\\Longleftrightarrow\\quad\\cos\\alpha=0";
  const reciprocal = 1 / target;
  const reciprocalStart = Math.abs(Math.abs(target) - Math.sqrt(3)) < 1e-9
    ? `${target < 0 ? "-" : ""}\\dfrac{1}{\\sqrt3}=`
    : "";
  return `\\operatorname{ctg}\\alpha=${exactValueLatex(target)}\\quad\\Longleftrightarrow\\quad\\operatorname{tg}\\alpha=${reciprocalStart}${exactValueLatex(reciprocal)}`;
}

function baseEquationLatex(data, graphFn, graphTarget) {
  if (data.fn === "cot") {
    const fnLatex = graphFn === "tan" ? "\\operatorname{tg}" : `\\${graphFn}`;
    return `${fnLatex} u=${exactValueLatex(graphTarget)}`;
  }
  if (data.baseEquation) return data.baseEquation;
  const fnLatex = graphFn === "tan" ? "\\operatorname{tg}" : `\\${graphFn}`;
  return `${fnLatex} u=${exactValueLatex(graphTarget)}`;
}

function SvgMathLabel({ x, y, math, width = 64, height = 34, fontSize = 12, align = "center" }) {
  const left = align === "left" ? x : align === "right" ? x - width : x - width / 2;
  const justifyContent = align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
  return <foreignObject x={left} y={y - height / 2} width={width} height={height} overflow="visible" aria-hidden="true">
    <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent, color: "currentColor", fontSize, lineHeight: 1 }}>
      <InlineMath math={math} />
    </div>
  </foreignObject>;
}

export function TrigEquationGraph({ fn = "sin", target = 0, families = [], expected = null }) {
  const width = 620;
  const height = 300;
  const pad = { left: 34, right: 26, top: 20, bottom: 46 };
  const xMin = -1.14 * Math.PI;
  const xMax = 1.14 * Math.PI;
  const isTangent = fn === "tan";
  const yLimit = fn === "sin" || fn === "cos" ? 1.35 : 2.25;
  const sx = (x) => pad.left + ((x - xMin) / (xMax - xMin)) * (width - pad.left - pad.right);
  const sy = (y) => pad.top + ((yLimit - y) / (2 * yLimit)) * (height - pad.top - pad.bottom);
  const graphSolutions = expected || solutionsInInterval(families, [rat(-1), rat(1)]);
  let path = "";
  let drawing = false;
  for (let i = 0; i <= 850; i += 1) {
    const x = xMin + ((xMax - xMin) * i) / 850;
    const y = evaluate(fn, x);
    const visible = Number.isFinite(y) && Math.abs(y) <= yLimit * 1.08;
    if (!visible) { drawing = false; continue; }
    path += `${drawing ? "L" : "M"}${sx(x).toFixed(2)},${sy(y).toFixed(2)} `;
    drawing = true;
  }
  const mainTicks = [[rat(-1), "-\\pi"], [rat(-1, 2), "-\\dfrac{\\pi}{2}"], [rat(0), "0"], [rat(1, 2), "\\dfrac{\\pi}{2}"], [rat(1), "\\pi"]];
  const yTicks = isTangent
    ? [[-1, "-1"], [1, "1"]]
    : [[-1, "-1"], [1, "1"]];
  const targetHasMainLabel = Math.abs(target) < 1e-9 || Math.abs(Math.abs(target) - 1) < 1e-9;
  const targetIsThirdRoot = Math.abs(Math.abs(target) - Math.sqrt(3) / 3) < 1e-9;
  const asymptotes = [];
  if (isTangent) {
    for (let k = -10; k <= 10; k += 1) {
      const x = Math.PI / 2 + k * Math.PI;
      if (x > xMin && x < xMax && !asymptotes.some((saved) => Math.abs(saved - x) < 1e-7)) asymptotes.push(x);
    }
  }
  const minorTicks = [];
  for (let n = -5; n <= 5; n += 1) minorTicks.push(rat(n, 6));
  return (
    <div style={{ width: "100%", maxWidth: width, margin: "14px auto", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Wykres równania trygonometrycznego" style={{ display: "block", minWidth: 520, color: "var(--color-text)" }}>
        <defs><marker id="trig-arrow" markerWidth="8" markerHeight="8" refX="5" refY="3" orient="auto-start-reverse" markerUnits="strokeWidth"><path d="M0,0 L0,6 L6,3 z" fill="currentColor" /></marker></defs>
        <line x1={pad.left + 3} y1={sy(0)} x2={width - pad.right - 3} y2={sy(0)} stroke="currentColor" strokeWidth="1.5" markerStart="url(#trig-arrow)" markerEnd="url(#trig-arrow)" />
        <line x1={sx(0)} y1={height - pad.bottom} x2={sx(0)} y2={pad.top + 3} stroke="currentColor" strokeWidth="1.5" markerEnd="url(#trig-arrow)" />
        {minorTicks.map((value) => <line key={ratKey(value)} x1={sx(ratValue(value) * Math.PI)} y1={sy(0) - 3} x2={sx(ratValue(value) * Math.PI)} y2={sy(0) + 3} stroke="currentColor" opacity="0.45" />)}
        {mainTicks.map(([value, label]) => <g key={label}><line x1={sx(ratValue(value) * Math.PI)} y1={sy(0) - 5} x2={sx(ratValue(value) * Math.PI)} y2={sy(0) + 5} stroke="currentColor" /><SvgMathLabel x={sx(ratValue(value) * Math.PI)} y={height - 16} math={label} fontSize={13} /></g>)}
        {yTicks.map(([y, label]) => <g key={label}><line x1={sx(0) - 4} y1={sy(y)} x2={sx(0) + 4} y2={sy(y)} stroke="currentColor" /><SvgMathLabel x={sx(0) - 9} y={sy(y)} math={label} width={28} fontSize={isTangent ? 10 : 12} align="right" /></g>)}
        {isTangent && !targetHasMainLabel && <g color="#a78bfa" aria-label={targetIsThirdRoot ? (target < 0 ? "minus pierwiastek z trzech przez trzy" : "pierwiastek z trzech przez trzy") : exactValueLatex(target)}>
          <SvgMathLabel x={sx(0) - 9} y={sy(target)} math={exactValueLatex(target)} width={56} height={40} fontSize={11} align="right" />
        </g>}
        {asymptotes.map((x, index) => <line key={`asymptote-${index}`} x1={sx(x)} y1={pad.top} x2={sx(x)} y2={height - pad.bottom} stroke="currentColor" strokeWidth="1" strokeDasharray="4 5" opacity="0.35" />)}
        <path d={path} fill="none" stroke="#e58b00" strokeWidth="3" />
        {Math.abs(target) <= yLimit && <line x1={pad.left} y1={sy(target)} x2={width - pad.right} y2={sy(target)} stroke="#7c3aed" strokeWidth="2" strokeDasharray="7 5" />}
        {graphSolutions.map((value, index) => {
          const x = ratValue(value) * Math.PI;
          const labelY = sy(0) + (target >= 0 ? 20 : -10);
          return <g key={`${ratKey(value)}-${index}`}><line x1={sx(x)} y1={sy(target)} x2={sx(x)} y2={sy(0)} stroke="#7c3aed" strokeWidth="1" strokeDasharray="3 4" opacity="0.75" /><circle cx={sx(x)} cy={sy(target)} r="5" fill="#7c3aed" stroke="white" strokeWidth="1.5" /><g color="#a78bfa"><SvgMathLabel x={sx(x)} y={labelY} math={piFractionLatex(value)} width={62} height={38} fontSize={11} /></g></g>;
        })}
        <SvgMathLabel x={width - pad.right - 2} y={sy(0) - 12} math="x" width={18} height={24} fontSize={14} align="right" />
        <SvgMathLabel x={sx(0) + 9} y={pad.top + 10} math="y" width={18} height={24} fontSize={14} align="left" />
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  const legacyInterval = !Array.isArray(data.families) && Array.isArray(data.expected);
  return <div style={{ lineHeight: 1.7 }}><p style={{ marginBottom: 6 }}>{data.interval ? <>Rozwiąż równanie w przedziale <InlineMath math={intervalLatex(data.interval)} />.</> : legacyInterval ? <>Rozwiąż równanie w przedziale <InlineMath math={"[-\\pi,\\pi]"} />.</> : "Rozwiąż równanie."}</p><BlockMath math={data.equationLatex.replace("\\left(3(x+2\\pi)\\right)", "\\left(3x+6\\pi\\right)")} /></div>;
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const legacyInterval = !Array.isArray(data.families) && Array.isArray(data.expected);
  const value = userAnswer || {};
  const families = Array.isArray(value.families) ? value.families : [];
  const xs = Array.isArray(value.xs) ? value.xs : [];
  const selectedCount = (count) => Number.isInteger(count) ? count : 0;
  const countValue = (count) => Number.isInteger(count) ? count : "";
  const countOptions = (limit) => Array.from({ length: limit }, (_, index) => ({ value: index + 1, label: String(index + 1) }));
  const setFamily = (index, nextValue) => {
    const next = [...families];
    next[index] = nextValue;
    onChange?.({ ...value, families: next });
  };
  const setX = (index, nextValue) => {
    const next = [...xs];
    next[index] = nextValue;
    onChange?.({ ...value, xs: next });
  };
  const familyFields = (heading) => <div style={{ display: "grid", gap: 10 }}>
    <div>{heading}</div>
    <div style={{ maxWidth: 260 }}>
      <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Wybierz liczbę rodzin rozwiązań:</div>
      <SelectInput value={countValue(value.familyCount)} onChange={(event) => onChange?.({ ...value, familyCount: event.target.value === "" ? "" : Number(event.target.value) })} disabled={disabled} options={countOptions(2)} />
    </div>
    {Array.from({ length: selectedCount(value.familyCount) }).map((_, index) => <div key={index} style={{ display: "grid", gap: 5 }}><div><InlineMath math={`x_${index + 1}=`} /></div><PiInput value={families[index]} onChange={(nextValue) => setFamily(index, nextValue)} disabled={disabled} label={`x_${index + 1}`} placeholder="np. pi/6+k*pi" allowAffine affineSetPreview /></div>)}
  </div>;

  if (legacyInterval) {
    return <div style={{ display: "grid", gap: 12 }}><div>Wpisz wszystkie rozwiązania z podanego przedziału.</div>{data.expected.map((_, index) => <div key={index} style={{ display: "flex", gap: 8, alignItems: "flex-start", flexWrap: "wrap" }}><InlineMath math={`x_${index + 1}=`} /><PiInput value={xs[index]} onChange={(nextValue) => setX(index, nextValue)} disabled={disabled} label={`x_${index + 1}`} placeholder="np. -pi/2" /></div>)}</div>;
  }

  if (data.interval) {
    return <div style={{ display: "grid", gap: 22 }}>
      {familyFields(<><strong>Krok 1 (1 pkt).</strong> Podaj rozwiązanie ogólne, wpisując także okres przy <InlineMath math={"k"} />. Informację <InlineMath math={"k\\in\\mathbb{Z}"} /> dopiszemy w podglądzie automatycznie.</>)}
      <div style={{ display: "grid", gap: 10 }}>
        <div><strong>Krok 2 (1 pkt).</strong> Wybierz z rozwiązania ogólnego wszystkie wartości należące do <InlineMath math={intervalLatex(data.interval)} />. Kolejność nie ma znaczenia.</div>
        <div style={{ maxWidth: 260 }}>
          <div style={{ fontSize: 12, opacity: 0.85, marginBottom: 6 }}>Wybierz liczbę rozwiązań:</div>
          <SelectInput value={countValue(value.solutionCount)} onChange={(event) => onChange?.({ ...value, solutionCount: event.target.value === "" ? "" : Number(event.target.value) })} disabled={disabled} options={countOptions(5)} />
        </div>
        {Array.from({ length: selectedCount(value.solutionCount) }).map((_, index) => <div key={index} style={{ display: "grid", gap: 5 }}><div><InlineMath math={`x_${index + 1}=`} /></div><PiInput value={xs[index]} onChange={(nextValue) => setX(index, nextValue)} disabled={disabled} label={`x_${index + 1}`} placeholder="np. -pi/2" /></div>)}
      </div>
    </div>;
  }

  return familyFields(<>Wpisz rozwiązanie ogólne, np. <InlineMath math={"x=\\frac{\\pi}{6}+k\\pi"} />. Informację <InlineMath math={"k\\in\\mathbb{Z}"} /> dopiszemy w podglądzie automatycznie.</>);
}

const parsedPiValue = (value) => {
  if (value && typeof value === "object" && Number.isFinite(value.parsed)) return value.parsed;
  const raw = value && typeof value === "object" ? value.raw : value;
  return parsePiLikeInput(raw);
};

function familyEquivalent(actual, expected) {
  const parsed = actual?.parsed;
  if (!parsed || !Number.isFinite(parsed.a) || !Number.isFinite(parsed.b)) return false;
  const expectedAnchor = ratValue(expected.anchor) * Math.PI;
  const expectedPeriod = Math.abs(ratValue(expected.period) * Math.PI);
  if (Math.abs(Math.abs(parsed.b) - expectedPeriod) > 1e-6) return false;
  const turns = (parsed.a - expectedAnchor) / expectedPeriod;
  return Math.abs(turns - Math.round(turns)) < 1e-6;
}

function familiesCorrect(data, userAnswer) {
  const declaredCount = Number.isInteger(userAnswer?.familyCount) ? userAnswer.familyCount : 0;
  if (declaredCount !== data.families.length) return false;
  const answers = Array.isArray(userAnswer?.families) ? userAnswer.families.slice(0, declaredCount) : [];
  if (answers.length !== data.families.length) return false;
  const used = new Set();
  return data.families.every((expected) => {
    const index = answers.findIndex((answer, answerIndex) => !used.has(answerIndex) && familyEquivalent(answer, expected));
    if (index < 0) return false;
    used.add(index);
    return true;
  });
}

function intervalSolutionsCorrect(data, userAnswer) {
  const declaredCount = Number.isInteger(userAnswer?.solutionCount) ? userAnswer.solutionCount : 0;
  if (declaredCount !== data.expected.length) return false;
  const values = Array.isArray(userAnswer?.xs) ? userAnswer.xs.slice(0, declaredCount).map(parsedPiValue) : [];
  if (values.length !== data.expected.length || values.some((value) => !Number.isFinite(value))) return false;
  const actual = [...values].sort((x, y) => x - y);
  const expected = data.expected.map((value) => ratValue(value) * Math.PI).sort((x, y) => x - y);
  return expected.every((value, index) => Math.abs(value - actual[index]) < 1e-6);
}

export function checkAnswer({ data }, userAnswer) {
  if (!Array.isArray(data.families) && Array.isArray(data.expected)) {
    const values = Array.isArray(userAnswer?.xs) ? userAnswer.xs.map(parsedPiValue) : [];
    if (values.length !== data.expected.length || values.some((value) => !Number.isFinite(value))) return { correct: false, isCorrect: false, score: 0, maxScore: 1, message: "Wpisz wszystkie rozwiązania." };
    const actual = [...values].sort((x, y) => x - y);
    const expected = data.expected.map((value) => ratValue(value) * Math.PI).sort((x, y) => x - y);
    const correct = expected.every((value, index) => Math.abs(value - actual[index]) < 1e-6);
    return { correct, isCorrect: correct, score: correct ? 1 : 0, maxScore: 1, message: correct ? "Dobrze!" : "Sprawdź rozwiązanie ogólne i granice przedziału." };
  }
  const generalOk = familiesCorrect(data, userAnswer);
  if (data.interval) {
    const intervalOk = intervalSolutionsCorrect(data, userAnswer);
    const score = Number(generalOk) + Number(intervalOk);
    return { correct: score === 2, isCorrect: score === 2, score, maxScore: 2, parts: { "rozwiązanie ogólne": generalOk, "rozwiązania z przedziału": intervalOk }, message: score === 2 ? "Dobrze!" : "Sprawdź rozwiązanie ogólne, liczbę wyników i granice przedziału." };
  }
  return { correct: generalOk, isCorrect: generalOk, score: generalOk ? 1 : 0, maxScore: 1, message: generalOk ? "Dobrze!" : "Sprawdź liczbę rodzin, kąty bazowe i okres funkcji." };
}

export function renderAnswer({ data }) {
  if (!Array.isArray(data.families) && Array.isArray(data.expected)) return <InlineMath math={`x\\in${solutionSetLatex(data.expected)}`} />;
  if (data.interval) return <div style={{ display: "grid", gap: 6 }}><div><strong>Rozwiązanie ogólne:</strong> <InlineMath math={`${familiesLatex(data.families)},\\qquad k\\in\\mathbb{Z}`} /></div><div><strong>W podanym przedziale:</strong> <InlineMath math={`x\\in${solutionSetLatex(data.expected)}`} /></div></div>;
  return <><InlineMath math={familiesLatex(data.families)} />, <InlineMath math={"k\\in\\mathbb{Z}"} /></>;
}

export function renderExplanation({ data }) {
  if (!Array.isArray(data.families) && Array.isArray(data.expected)) {
    return <div style={{ textAlign: "left", lineHeight: 1.7 }}><p>To zadanie pochodzi z wcześniejszej wersji zestawu. Po odświeżeniu strony zostanie zastąpione zadaniem z rozwiązaniem ogólnym.</p><BlockMath math={`x\\in${solutionSetLatex(data.expected)}`} /></div>;
  }
  const isCotangent = data.fn === "cot";
  const isZeroCotangent = isCotangent && Math.abs(data.target) < 1e-9;
  const beforeGraphSteps = data.beforeGraphSteps || [];
  const afterGraphSteps = data.afterGraphSteps || [];
  const hasIsolation = beforeGraphSteps.length > 0;
  const graphStep = 1 + Number(isCotangent) + Number(hasIsolation);
  const algebraStep = graphStep + 1;
  const generalStep = algebraStep + Number(afterGraphSteps.length > 0);
  const intervalStep = generalStep + 1;
  const graphFn = isZeroCotangent ? "cos" : isCotangent ? "tan" : data.fn;
  const graphTarget = isZeroCotangent ? 0 : isCotangent ? 1 / data.target : data.target;
  const baseFamilies = data.baseFamilies || data.families;
  const graphSolutions = solutionsInInterval(baseFamilies, [rat(-1), rat(1)]);
  const graphFunctionName = { sin: "sinusa", cos: "cosinusa", tan: "tangensa" }[graphFn] || "funkcji";
  return <div style={{ textAlign: "left", lineHeight: 1.7 }}>
    {isCotangent && <><p><strong>Krok 1.</strong> {isZeroCotangent ? "Dla wartości zero przechodzimy do cosinusa. Cotangens jest równy zero dokładnie wtedy, gdy licznik ilorazu, czyli cosinus, jest równy zero." : "Zamieniamy cotangens na tangens przez odwrócenie prawej strony. Dzięki temu korzystamy z wykresu tangensa dostępnego w karcie wzorów CKE."}</p><BlockMath math={cotangentConversionLatex(data.target)} /></>}
    {hasIsolation && <><p><strong>Krok {1 + Number(isCotangent)}.</strong> Izolujemy funkcję trygonometryczną. Przekształcenia wykonujemy algebraicznie, bez zmieniania wykresu.</p>{beforeGraphSteps.map((step, index) => <BlockMath key={index} math={step} />)}</>}
    <p><strong>Krok {graphStep}.</strong> Cały argument oznaczamy przez <InlineMath math={"u"} /> i rozwiązujemy równanie bazowe:</p>
    {data.argumentLatex && <BlockMath math={`u=${data.argumentLatex},\\qquad ${baseEquationLatex(data, graphFn, graphTarget)}`} />}
    {!data.argumentLatex && <BlockMath math={baseEquationLatex(data, graphFn, graphTarget)} />}
    <p>Zawsze korzystamy z nieprzekształconego wykresu {graphFunctionName} od <InlineMath math={"-\\pi"} /> do <InlineMath math={"\\pi"} />. Punkty przecięcia z poziomą prostą dają wszystkie kąty bazowe.</p>
    <TrigEquationGraph fn={graphFn} target={graphTarget} families={baseFamilies} expected={graphSolutions} />
    <p>Z wykresu odczytujemy i zapisujemy osobno każdą rodzinę rozwiązań:</p>
    <BlockMath math={`${baseFamiliesLatex(baseFamilies)},\\qquad k\\in\\mathbb{Z}`} />
    {afterGraphSteps.length > 0 && <><p><strong>Krok {algebraStep}.</strong> Wracamy do właściwego argumentu i rozwiązujemy każde otrzymane równanie względem <InlineMath math={"x"} />.</p>{afterGraphSteps.map((step, index) => <BlockMath key={index} math={step} />)}</>}
    <p><strong>Krok {generalStep}.</strong> Dopisujemy okres i otrzymujemy rozwiązanie ogólne:</p>
    <BlockMath math={`${familiesLatex(data.families)},\\qquad k\\in\\mathbb{Z}`} />
    {data.interval && <><p><strong>Krok {intervalStep}.</strong> Podstawiamy kolejne liczby całkowite <InlineMath math={"k"} /> i wybieramy wartości należące do <InlineMath math={intervalLatex(data.interval)} />.</p><BlockMath math={`x\\in${solutionSetLatex(data.expected)}`} /></>}
  </div>;
}

export function renderUserAnswer(userAnswer) {
  const lines = Array.isArray(userAnswer?.families) ? userAnswer.families.slice(0, Number.isInteger(userAnswer?.familyCount) ? userAnswer.familyCount : undefined).map((value, index) => { const raw = value && typeof value === "object" ? value.raw : value; return formatAffineLatexPreview(String(raw ?? ""), `x_${index + 1}`, true); }).filter(Boolean) : [];
  const xs = Array.isArray(userAnswer?.xs) ? userAnswer.xs : [];
  const latex = xs.slice(0, Number.isInteger(userAnswer?.solutionCount) ? userAnswer.solutionCount : undefined).map((value) => { const raw = value && typeof value === "object" ? value.raw : value; return formatPiValueLatex(String(raw ?? "")); }).filter(Boolean);
  if (!lines.length && !latex.length) return <i>(brak odpowiedzi)</i>;
  return <div style={{ display: "grid", gap: 4 }}>{lines.length > 0 && <InlineMath math={lines.join("\\qquad\\lor\\qquad{}")} />}{latex.length > 0 && <InlineMath math={`x\\in\\left\\{${latex.join(",\\;")}\\right\\}`} />}</div>;
}

export default { type, generateTask, renderTask, renderInput, checkAnswer, renderAnswer, renderExplanation, renderUserAnswer };
